/**
 * JAISIU-3320 / JAISIU-3342 — Durable session resume (Pillar I).
 *
 * IndexedDB wrapper for three pieces of state that must survive a reload:
 *
 *   - drafts (`chatDraftBySession`)        — keyed by sessionKey
 *   - subagent stash (parentKey → cards)  — keyed by parent sessionKey
 *   - pinnedActiveJob (string | null)     — single record keyed "current"
 *
 * All functions are tolerant of missing/disabled IDB (Safari private mode,
 * SSR test environments, browsers that block storage). When IDB is
 * unavailable they return `null`/no-op and the caller continues to work
 * with its in-memory store.
 *
 * Session keys are trimmed before persistence so callers don't accidentally
 * fragment the namespace with whitespace. Empty/blank keys are treated as
 * "no-op" — they never reach IDB.
 */

import { MtimeCache } from "../../../../src/utils/mtime-cache.js";
import type { ChatSubagentCard } from "../ui-types";

export type IdbDraftEntry = {
  message: string;
  attachments: unknown[];
  updatedAt: number;
};

export type IdbPinnedActiveJob = {
  id: "current";
  sessionKey: string | null;
  updatedAt: number;
};

const DB_NAME = "jaisiu-session-cache";
const DB_VERSION = 3;
const STORE_DRAFTS = "drafts";
const STORE_SUBAGENT_STASH = "subagentStash";
const STORE_PINNED = "pinnedActiveJob";
const STORE_SCROLL_ANCHOR = "scrollAnchor";
const STORE_ATTACHMENT_PERSIST = "attachmentPersist";
const PINNED_KEY = "current";

/**
 * JAISIU-3474 — persisted scroll anchor for mid-thread position
 * restoration. The anchor is a stable per-message id (so it survives
 * any DOM reflow) plus the operator's offset-within-message in
 * pixels (so we can restore exactly where they were reading, not
 * just the message's top edge). The record is keyed by sessionKey
 * so a stale anchor from a previous session cannot leak across
 * tabs.
 */
/**
 * JAISIU-3533+ — authoritative scroll-anchor schema for the
 * chat-restore-rebuild epic. See idb-session-cache.ts doc-block above
 * for the full contract.
 *
 *   `viewportOffset`     px from top of message element to top of viewport
 *                        (legacy; kept for back-compat. New writers
 *                        populate `positionRatio` / `sectionIndex`
 *                        which describe the position relative to the
 *                        viewport CENTER, not the top).
 *   `positionRatio`      0..1, fractional position of the reading row
 *                        inside the viewport — `(viewportCenter -
 *                        rowTop) / rowHeight`. JAISIU-3533-update:
 *                        restore anchors the message at this position
 *                        relative to the viewport CENTER, so a long
 *                        answer the operator was reading at section
 *                        4/5 lands back on section 4/5 instead of the
 *                        top edge.
 *   `sectionIndex`       integer 0..4, quantisation of `positionRatio`
 *                        into 5 equal buckets (0%, 20%, 40%, 60%,
 *                        80-100%). Stored alongside `positionRatio`
 *                        so the algorithm can short-circuit to a
 *                        bucketed placement when the row's final
 *                        height at restore time differs from the
 *                        captured session.
 *   `viewportHeight`     px height of the viewport at capture time.
 *                        Needed because the restore math
 *                        (`target = rowCenter + viewportHalf -
 *                        clientHalf`) depends on both halves; if the
 *                        operator resized the window between capture
 *                        and restore, we still want the same
 *                        proportional position.
 *   `capturedAt`         Date.now() — diagnostic only, never used for
 *                        restore decisions.
 *   `transcriptRevision` monotonic counter / commit hash for the
 *                        transcript slice used.
 */
export type IdbScrollAnchorEntry = {
  sessionKey: string;
  messageId: string;
  /** @deprecated use positionRatio / sectionIndex; kept for back-compat. */
  viewportOffset: number;
  /**
   * JAISIU-types-sweep: make the post-deprecation fields optional on
   * the type so test fixtures and saved records written by older
   * gateway versions (which pre-date the positionRatio / sectionIndex
   * migration) can be loaded without a runtime cast. The save path
   * already fills sensible defaults (`positionRatio = 0.5`,
   * `sectionIndex = 2`, `transcriptRevision` keyed off `capturedAt`)
   * and the load path coalesces missing fields — the only thing
   * keeping them required on the type was the read-write asymmetry
   * between callers, which optional fields resolve.
   */
  positionRatio?: number;
  sectionIndex?: 0 | 1 | 2 | 3 | 4;
  viewportHeight?: number;
  /**
   * JAISIU-types-sweep: timestamp the scroll position was recorded
   * at. Optional because legacy `viewportOffset`-only records
   * didn't carry one — the load path coalesces missing values to
   * `Date.now()` so the chat-render-time "is this anchor fresh
   * enough?" check still works against migrated data.
   */
  capturedAt?: number;
  transcriptRevision?: string;
  /**
   * JAISIU-types-sweep: legacy scroll-anchor offset field, predates
   * the positionRatio migration. Kept as an optional field on the
   * type so the load path can fall back to it when
   * `positionRatio` is absent on legacy IDB records. Optional — new
   * writes use `positionRatio`, this is read-compat only.
   */
  offsetWithinMessage?: number;
  /**
   * JAISIU-types-sweep: generic updatedAt timestamp from the IDB
   * record (when the row was last written). Used by the load path
   * as a `capturedAt` fallback for legacy entries that pre-date the
   * scroll-anchor capture field. Not produced by the save path —
   * IndexedDB fills it automatically.
   */
  updatedAt?: number;
};

/**
 * FIFO cap on the number of sessions whose anchor we retain in IDB.
 * Picked at 256 because that comfortably covers any realistic operator
 * workload (most operators have <100 active chats/month) and keeps the
 * cursor's `.openCursor()` enumeration cheap.
 */
export const SCROLL_ANCHOR_FIFO_CAP = 256;

function hasIdb(): boolean {
  return (
    typeof globalThis !== "undefined" &&
    typeof (globalThis as { indexedDB?: IDBFactory }).indexedDB !== "undefined"
  );
}

function normalizeKey(key: string): string {
  return typeof key === "string" ? key.trim() : "";
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!hasIdb()) return Promise.reject(new Error("indexedDB unavailable"));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = (globalThis as { indexedDB: IDBFactory }).indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: "sessionKey" });
      }
      if (!db.objectStoreNames.contains(STORE_SUBAGENT_STASH)) {
        // Use a fixed "key" field as the primary key; the parent sessionKey
        // is stored under "parentKey" on the record so loaders can locate it.
        db.createObjectStore(STORE_SUBAGENT_STASH, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_PINNED)) {
        db.createObjectStore(STORE_PINNED, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_SCROLL_ANCHOR)) {
        // JAISIU-3474 — scroll-anchor store, keyed by sessionKey so
        // a refresh never lands on a stale anchor from another chat.
        db.createObjectStore(STORE_SCROLL_ANCHOR, { keyPath: "sessionKey" });
      }
      if (!db.objectStoreNames.contains(STORE_ATTACHMENT_PERSIST)) {
        // JAISIU-3768 — rendered attachment list per message, keyed
        // by messageId so a refresh + scroll-anchor restore brings
        // back the inline image and the chip exactly where the
        // operator left them.
        db.createObjectStore(STORE_ATTACHMENT_PERSIST, { keyPath: "messageId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IDB open failed"));
    req.onblocked = () => reject(new Error("IDB upgrade blocked"));
  });
  return dbPromise;
}

async function runOp<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | { complete: true; value: T },
): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const req = fn(store);
      if ("onsuccess" in req) {
        req.onsuccess = () => resolve((req.result as T) ?? null);
        req.onerror = () => resolve(null);
      } else {
        resolve(req.value);
      }
      tx.oncomplete = () => {
        // best-effort; result already resolved above
      };
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    });
  } catch {
    return null;
  }
}

// ─── Drafts ─────────────────────────────────────────────────────────────────

export async function idbLoadDraft(sessionKey: string): Promise<IdbDraftEntry | null> {
  const key = normalizeKey(sessionKey);
  if (!key) return null;
  const got =
    (await runOp<IdbDraftEntry | undefined>(STORE_DRAFTS, "readonly", (store) => store.get(key))) ??
    null;
  if (!got) return null;
  return {
    message: got.message ?? "",
    attachments: Array.isArray(got.attachments) ? got.attachments : [],
    updatedAt: typeof got.updatedAt === "number" ? got.updatedAt : 0,
  };
}

/**
 * How many draft records the `drafts` store keeps.
 *
 * The store is keyed by session key and previously had no cap and no
 * eviction at all — `idbDeleteDraft` only fired when a tab was closed, so
 * every session an operator ever opened left a row behind. Each row holds a
 * `ChatAttachment[]`, and before the session-file rework those attachments
 * carried base64 `dataUrl` strings (~85 KB per small file), so the residue
 * persisted in the browser profile indefinitely.
 *
 * 64 is deliberately generous: a draft is cheap (empty for most sessions)
 * and losing one is merely an inconvenience, whereas unbounded growth in a
 * durable store is what turned a memory problem into a profile problem.
 */
export const DRAFTS_FIFO_CAP = 64;

/**
 * Enforce {@link DRAFTS_FIFO_CAP} by walking `updatedAt` ascending and
 * deleting the oldest rows. Same shape as
 * {@link idbEnforceScrollAnchorCap} — the existing precedent for capping an
 * IDB store by recency. Returns the number of evicted rows.
 */
export async function idbEnforceDraftsCap(): Promise<number> {
  if (!hasIdb()) return 0;
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return 0;
  }
  return await new Promise<number>((resolve) => {
    let deleted = 0;
    try {
      const tx = db.transaction(STORE_DRAFTS, "readwrite");
      const store = tx.objectStore(STORE_DRAFTS);
      const countReq = store.count();
      countReq.onsuccess = () => {
        const total = typeof countReq.result === "number" ? countReq.result : 0;
        const toEvict = Math.max(0, total - DRAFTS_FIFO_CAP);
        if (toEvict === 0) {
          resolve(0);
          return;
        }
        // Collect then sort, so we delete the genuinely oldest rows rather
        // than whatever the key order happens to be.
        const rows: Array<{ key: string; updatedAt: number }> = [];
        const collectReq = store.openCursor();
        collectReq.onsuccess = () => {
          const cursor = collectReq.result;
          if (!cursor) {
            rows.sort((a, b) => a.updatedAt - b.updatedAt);
            for (const row of rows.slice(0, toEvict)) {
              store.delete(row.key);
              deleted += 1;
            }
            resolve(deleted);
            return;
          }
          const v = cursor.value as Partial<IdbDraftEntry>;
          rows.push({
            key: String(cursor.key),
            updatedAt: typeof v.updatedAt === "number" ? v.updatedAt : 0,
          });
          cursor.continue();
        };
        collectReq.onerror = () => resolve(0);
      };
      countReq.onerror = () => resolve(0);
    } catch {
      resolve(deleted);
    }
  });
}

export async function idbSaveDraft(sessionKey: string, entry: IdbDraftEntry): Promise<void> {
  const key = normalizeKey(sessionKey);
  if (!key) return;
  await runOp(STORE_DRAFTS, "readwrite", (store) =>
    store.put({
      sessionKey: key,
      message: entry.message,
      attachments: Array.isArray(entry.attachments) ? entry.attachments : [],
      updatedAt: entry.updatedAt ?? Date.now(),
    }),
  );
  // Fire-and-forget: a cap failure must never fail the save, which the
  // caller awaits on a synchronous-looking path.
  void idbEnforceDraftsCap();
}

export async function idbDeleteDraft(sessionKey: string): Promise<void> {
  const key = normalizeKey(sessionKey);
  if (!key) return;
  await runOp(STORE_DRAFTS, "readwrite", (store) => store.delete(key));
}

// ─── Subagent stash ─────────────────────────────────────────────────────────

export async function idbLoadSubagentStash(parentKey: string): Promise<ChatSubagentCard[] | null> {
  const key = normalizeKey(parentKey);
  if (!key) return null;
  const got =
    (await runOp<{ cards?: ChatSubagentCard[] } | undefined>(
      STORE_SUBAGENT_STASH,
      "readonly",
      (store) => store.get(key),
    )) ?? null;
  if (!got || !Array.isArray(got.cards)) return null;
  return got.cards;
}

export async function idbSaveSubagentStash(
  parentKey: string,
  cards: ChatSubagentCard[],
): Promise<void> {
  const key = normalizeKey(parentKey);
  if (!key) return;
  await runOp(STORE_SUBAGENT_STASH, "readwrite", (store) =>
    store.put({
      key,
      parentKey: key,
      cards: Array.isArray(cards) ? cards : [],
      updatedAt: Date.now(),
    }),
  );
}

// ─── Pinned active job ──────────────────────────────────────────────────────

export async function idbLoadPinnedActiveJob(): Promise<string | null> {
  const entry =
    (await runOp<IdbPinnedActiveJob | undefined>(STORE_PINNED, "readonly", (store) =>
      store.get(PINNED_KEY),
    )) ?? null;
  const sk = entry?.sessionKey ?? null;
  if (typeof sk !== "string") return null;
  const trimmed = sk.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export async function idbSavePinnedActiveJob(sessionKey: string | null): Promise<void> {
  const trimmed = typeof sessionKey === "string" ? sessionKey.trim() : "";
  await runOp(STORE_PINNED, "readwrite", (store) =>
    store.put({
      key: PINNED_KEY,
      id: PINNED_KEY,
      sessionKey: trimmed.length > 0 ? trimmed : null,
      updatedAt: Date.now(),
    }),
  );
}

// ─── Test helpers ───────────────────────────────────────────────────────────

/**
 * Reset module-level state so tests can start from a clean slate. Not part
 * of the public surface; only exported for tests.
 */
export function __resetIdbSessionCacheForTests(): void {
  dbPromise = null;
  scrollAnchorCache.clear();
}

// ─── Scroll anchor (JAISIU-3474) ───────────────────────────────────────────
//
// Persisted scroll position keyed by sessionKey. The anchor stores the
// id of the topmost visible message when the operator paused scrolling
// (or when a programmatic scroll settled). On reload, the chat-history
// loader consults this record to decide whether to drain older pages
// (when the anchor message is older than the initial latest-N window)
// and where to place the viewport after Lit commits the rendered DOM.
//
// Stale anchors from a previous session are explicitly scoped by
// sessionKey so a refresh after switching tabs cannot land the
// operator in the wrong chat at the wrong position.
//
// JAISIU-3496 / G5 — the decoded anchor is cached in an in-process
// `MtimeCache<IdbScrollAnchorEntry>` so a second boot (or a session
// switch within the same tab) skips the clamp/floor/trim decode when
// the IDB record hasn't been touched since the last read. The cache is
// keyed on `(sessionKey, updatedAt)` — `updatedAt` is the persisted
// timestamp we treat as mtimeMs. The IDB read itself still runs as a
// cheap probe (single `store.get`) so we can compare the stored mtime.

const scrollAnchorCache = new MtimeCache<IdbScrollAnchorEntry>();

export async function idbLoadScrollAnchor(
  sessionKey: string,
): Promise<IdbScrollAnchorEntry | null> {
  const key = normalizeKey(sessionKey);
  if (!key) return null;
  // Cheap probe — one IDB read per call. The persisted `updatedAt` is the
  // mtimeMs-equivalent we use to invalidate the in-process cache below.
  const got =
    (await runOp<IdbScrollAnchorEntry | undefined>(STORE_SCROLL_ANCHOR, "readonly", (store) =>
      store.get(key),
    )) ?? null;
  if (!got) {
    // Stale cache for a deleted record: drop it so a future re-save starts
    // clean. Mirrors the `verdictCache.delete` pattern in session-file-repair.
    scrollAnchorCache.delete(key);
    return null;
  }
  // JAISIU-3533+ — back-compat: older records may not have
  // positionRatio / sectionIndex / viewportHeight. We synthesise
  // sensible defaults from the legacy viewportOffset so a stale IDB
  // does not lose position on the day this schema lands:
  //   positionRatio  — fall back to 0.5 (mid-message) when absent,
  //                    because we genuinely do not know which
  //                    section the operator was on. The previous
  //                    `viewportOffset`-based restore mapped every
  //                    scroll position onto the row top, which
  //                    silently demoted long answers to their first
  //                    section; 0.5 is the safest mid-fallback.
  //   sectionIndex   — round(positionRatio * 4).
  //   viewportHeight — 0 is fine; the restore computes the viewport
  //                    half from clientHeight at restore time and
  //                    falls back to a fixed estimate when this is 0.
  const legacyOffset =
    typeof got.viewportOffset === "number"
      ? got.viewportOffset
      : typeof got.offsetWithinMessage === "number"
        ? got.offsetWithinMessage
        : 0;
  const legacyCaptured =
    typeof got.capturedAt === "number"
      ? got.capturedAt
      : typeof got.updatedAt === "number"
        ? got.updatedAt
        : 0;
  const rawRatio =
    typeof got.positionRatio === "number" && Number.isFinite(got.positionRatio)
      ? Math.max(0, Math.min(1, got.positionRatio))
      : 0.5;
  const rawSection =
    typeof got.sectionIndex === "number" && Number.isFinite(got.sectionIndex)
      ? Math.max(0, Math.min(4, Math.round(got.sectionIndex)))
      : Math.round(rawRatio * 4);
  const rawViewportHeight =
    typeof got.viewportHeight === "number" &&
    Number.isFinite(got.viewportHeight) &&
    got.viewportHeight > 0
      ? Math.floor(got.viewportHeight)
      : 0;
  const mtimeMs = legacyCaptured;
  const cached = scrollAnchorCache.get(key, mtimeMs);
  if (cached) return cached;
  const mid = typeof got.messageId === "string" ? got.messageId.trim() : "";
  if (!mid) return null;
  const decoded: IdbScrollAnchorEntry = {
    sessionKey: key,
    messageId: mid,
    viewportOffset: Number.isFinite(legacyOffset) ? Math.max(0, Math.floor(legacyOffset)) : 0,
    positionRatio: rawRatio,
    sectionIndex: rawSection as 0 | 1 | 2 | 3 | 4,
    viewportHeight: rawViewportHeight,
    capturedAt: legacyCaptured,
    transcriptRevision:
      typeof got.transcriptRevision === "string" && got.transcriptRevision.trim()
        ? got.transcriptRevision.trim()
        : "",
  };
  scrollAnchorCache.set(key, mtimeMs, decoded);
  return decoded;
}

export async function idbSaveScrollAnchor(entry: IdbScrollAnchorEntry): Promise<void> {
  const key = normalizeKey(entry.sessionKey);
  const mid = typeof entry.messageId === "string" ? entry.messageId.trim() : "";
  if (!key || !mid) return;
  const capturedAt = entry.capturedAt ?? Date.now();
  const viewportOffset =
    typeof entry.viewportOffset === "number" && Number.isFinite(entry.viewportOffset)
      ? Math.max(0, Math.floor(entry.viewportOffset))
      : 0;
  const positionRatio =
    typeof entry.positionRatio === "number" && Number.isFinite(entry.positionRatio)
      ? Math.max(0, Math.min(1, entry.positionRatio))
      : 0.5;
  const rawSection =
    typeof entry.sectionIndex === "number" && Number.isFinite(entry.sectionIndex)
      ? Math.max(0, Math.min(4, Math.round(entry.sectionIndex)))
      : Math.round(positionRatio * 4);
  const sectionIndex = rawSection as 0 | 1 | 2 | 3 | 4;
  const viewportHeight =
    typeof entry.viewportHeight === "number" &&
    Number.isFinite(entry.viewportHeight) &&
    entry.viewportHeight > 0
      ? Math.floor(entry.viewportHeight)
      : 0;
  const transcriptRevision =
    typeof entry.transcriptRevision === "string" && entry.transcriptRevision.trim()
      ? entry.transcriptRevision.trim()
      : String(capturedAt);
  await runOp(STORE_SCROLL_ANCHOR, "readwrite", (store) =>
    store.put({
      sessionKey: key,
      messageId: mid,
      viewportOffset,
      positionRatio,
      sectionIndex,
      viewportHeight,
      capturedAt,
      transcriptRevision,
    }),
  );
  // JAISIU-3533 — invalidate the in-process cache so the next read sees
  // the just-written record; the FIFO cap step below may also invalidate
  // evicted entries.
  scrollAnchorCache.set(key, capturedAt, {
    sessionKey: key,
    messageId: mid,
    viewportOffset,
    positionRatio,
    sectionIndex,
    viewportHeight,
    capturedAt,
    transcriptRevision,
  });
  // Best-effort FIFO eviction. The deletion runs out-of-band; failure
  // here must NOT break the put above.
  void idbEnforceScrollAnchorCap();
}

export async function idbDeleteScrollAnchor(sessionKey: string): Promise<void> {
  const key = normalizeKey(sessionKey);
  if (!key) return;
  await runOp(STORE_SCROLL_ANCHOR, "readwrite", (store) => store.delete(key));
}

/**
 * JAISIU-3533 — boot-time enumeration of every persisted scroll anchor.
 * Used by the chat-restore bootstrap to seed the in-memory map in one
 * IDB round-trip instead of N per-session reads. Returns anchors
 * ordered by `capturedAt` ascending — the order is informational only;
 * consumers re-key by `sessionKey` before use.
 */
export async function idbEnumerateScrollAnchors(): Promise<IdbScrollAnchorEntry[]> {
  if (!hasIdb()) return [];
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return [];
  }
  return await new Promise<IdbScrollAnchorEntry[]>((resolve) => {
    const out: IdbScrollAnchorEntry[] = [];
    try {
      const tx = db.transaction(STORE_SCROLL_ANCHOR, "readonly");
      const store = tx.objectStore(STORE_SCROLL_ANCHOR);
      const req = store.openCursor();
      req.onerror = () => resolve([]);
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          // JAISIU-types-sweep: `capturedAt` is optional on the type,
          // so the sort falls back to `0` when it's missing — the
          // enumeration itself doesn't depend on the timestamp, only
          // the rendered order does, and a missing timestamp gets a
          // deterministic position (cold-start at the back).
          resolve(
            out.sort((a, b) => (a.capturedAt ?? 0) - (b.capturedAt ?? 0)),
          );
          return;
        }
        const v = cursor.value as Partial<IdbScrollAnchorEntry> & {
          offsetWithinMessage?: number;
          updatedAt?: number;
        };
        const sessionKey = typeof v.sessionKey === "string" ? v.sessionKey.trim() : "";
        const messageId = typeof v.messageId === "string" ? v.messageId.trim() : "";
        if (sessionKey && messageId) {
          const legacyOffset =
            typeof v.viewportOffset === "number"
              ? v.viewportOffset
              : typeof v.offsetWithinMessage === "number"
                ? v.offsetWithinMessage
                : 0;
          const legacyCaptured =
            typeof v.capturedAt === "number"
              ? v.capturedAt
              : typeof v.updatedAt === "number"
                ? v.updatedAt
                : 0;
          out.push({
            sessionKey,
            messageId,
            viewportOffset: Number.isFinite(legacyOffset)
              ? Math.max(0, Math.floor(legacyOffset))
              : 0,
            capturedAt: legacyCaptured,
            transcriptRevision:
              typeof v.transcriptRevision === "string" && v.transcriptRevision.trim()
                ? v.transcriptRevision.trim()
                : "",
          });
        }
        cursor.continue();
      };
    } catch {
      resolve([]);
    }
  });
}

/**
 * JAISIU-3533 — enforce the FIFO cap of `SCROLL_ANCHOR_FIFO_CAP` records
 * in the scroll-anchor store. Walks the store by `capturedAt` ascending
 * and deletes the oldest until the count is at or below the cap.
 * Returns the number of evicted records.
 */
export async function idbEnforceScrollAnchorCap(): Promise<number> {
  if (!hasIdb()) return 0;
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return 0;
  }
  return await new Promise<number>((resolve) => {
    let deleted = 0;
    try {
      const tx = db.transaction(STORE_SCROLL_ANCHOR, "readwrite");
      const store = tx.objectStore(STORE_SCROLL_ANCHOR);
      const req = store.count();
      req.onsuccess = () => {
        const total = typeof req.result === "number" ? req.result : 0;
        const toEvict = Math.max(0, total - SCROLL_ANCHOR_FIFO_CAP);
        if (toEvict === 0) {
          resolve(0);
          return;
        }
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || deleted >= toEvict) {
            resolve(deleted);
            return;
          }
          scrollAnchorCache.delete(cursor.key as string);
          cursor.delete();
          deleted += 1;
          cursor.continue();
        };
        cursorReq.onerror = () => resolve(deleted);
      };
      req.onerror = () => resolve(0);
    } catch {
      resolve(deleted);
    }
  });
}

// ─── Rendered attachment persistence (JAISIU-3768) ────────────────────────

/**
 * Per-message attachment list, persisted so a refresh + scroll-anchor
 * restore brings the inline image and the file chip back at the same
 * pixel position the operator was reading.
 *
 * The record stores `attachments` as `unknown[]` (the same shape as
 * `IdbDraftEntry.attachments`) and the wrapper module
 * `chat/chat-attachment-persistence.ts` narrows to the foundation
 * `ChatAttachment` type from `./attachment-types.ts`. This keeps the
 * IDB layer type-agnostic so it can be re-used for other schema
 * bumps without taking a dependency on the chat module.
 *
 * The `messageId` is the rendered-message id (the one the chat-thread-
 * render layer paints with `data-message-id`), not a transient draft
 * id. A message that has no attachments stores an empty array so the
 * next load short-circuits to `[]` instead of hitting the network.
 */
export type IdbAttachmentEntry = {
  messageId: string;
  sessionKey: string;
  attachments: unknown[];
  updatedAt: number;
};

/**
 * FIFO cap on the number of messages whose attachment list we retain.
 * Generous on purpose: a chat with many long messages keeps its image
 * and chip state for the visible window and a healthy buffer around
 * it. 4096 covers a heavy operator's history without unbounded growth.
 */
export const ATTACHMENT_PERSIST_FIFO_CAP = 4096;

/**
 * Read the attachment list for a single rendered message.
 *
 * Returns `null` when nothing is stored (first visit, IDB disabled,
 * or the record was evicted). Returns `[]` when the message is
 * recorded with no attachments — that distinction matters so the
 * caller can decide whether to fetch from the network or render
 * nothing.
 */
export async function idbLoadAttachments(
  messageId: string,
): Promise<IdbAttachmentEntry | null> {
  const key = normalizeKey(messageId);
  if (!key) return null;
  const got =
    (await runOp<IdbAttachmentEntry | undefined>(
      STORE_ATTACHMENT_PERSIST,
      "readonly",
      (store) => store.get(key),
    )) ?? null;
  if (!got) return null;
  return {
    messageId: typeof got.messageId === "string" ? got.messageId : key,
    sessionKey: typeof got.sessionKey === "string" ? got.sessionKey : "",
    attachments: Array.isArray(got.attachments) ? got.attachments : [],
    updatedAt: typeof got.updatedAt === "number" ? got.updatedAt : 0,
  };
}

/**
 * Bulk load the attachment lists for a list of message ids. Used by
 * the scroll-anchor restore path: when a session is rehydrated we
 * know exactly which messages are around the anchor and we can
 * prime the in-memory mirror in one transaction.
 *
 * Missing ids are silently dropped from the result; the caller is
 * expected to fall back to the network for the rest.
 */
export async function idbLoadAttachmentsMany(
  messageIds: readonly string[],
): Promise<Map<string, IdbAttachmentEntry>> {
  const out = new Map<string, IdbAttachmentEntry>();
  if (messageIds.length === 0) return out;
  const keys = messageIds
    .map((id) => normalizeKey(id))
    .filter((k): k is string => Boolean(k));
  if (keys.length === 0) return out;
  if (!hasIdb()) return out;
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return out;
  }
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_ATTACHMENT_PERSIST, "readonly");
      const store = tx.objectStore(STORE_ATTACHMENT_PERSIST);
      let pending = keys.length;
      const finish = () => {
        pending -= 1;
        if (pending === 0) resolve();
      };
      for (const key of keys) {
        const req = store.get(key);
        req.onsuccess = () => {
          const got = req.result as IdbAttachmentEntry | undefined;
          if (got) {
            out.set(key, {
              messageId: typeof got.messageId === "string" ? got.messageId : key,
              sessionKey:
                typeof got.sessionKey === "string" ? got.sessionKey : "",
              attachments: Array.isArray(got.attachments) ? got.attachments : [],
              updatedAt: typeof got.updatedAt === "number" ? got.updatedAt : 0,
            });
          }
          finish();
        };
        req.onerror = () => finish();
      }
    } catch {
      resolve();
    }
  });
  return out;
}

export async function idbSaveAttachments(entry: IdbAttachmentEntry): Promise<void> {
  const key = normalizeKey(entry.messageId);
  if (!key) return;
  await runOp(STORE_ATTACHMENT_PERSIST, "readwrite", (store) =>
    store.put({
      messageId: key,
      sessionKey: normalizeKey(entry.sessionKey),
      attachments: Array.isArray(entry.attachments) ? entry.attachments : [],
      updatedAt: entry.updatedAt ?? Date.now(),
    }),
  );
  void idbEnforceAttachmentPersistCap();
}

export async function idbDeleteAttachments(messageId: string): Promise<void> {
  const key = normalizeKey(messageId);
  if (!key) return;
  await runOp(STORE_ATTACHMENT_PERSIST, "readwrite", (store) => store.delete(key));
}

/**
 * Walk the store and evict the oldest rows (by `updatedAt`) until the
 * total is back at {@link ATTACHMENT_PERSIST_FIFO_CAP}. Returns the
 * number of rows evicted. Errors and IDB-disabled environments return
 * 0 — cap failure must never fail the save.
 */
export async function idbEnforceAttachmentPersistCap(): Promise<number> {
  if (!hasIdb()) return 0;
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return 0;
  }
  return await new Promise<number>((resolve) => {
    let deleted = 0;
    try {
      const tx = db.transaction(STORE_ATTACHMENT_PERSIST, "readwrite");
      const store = tx.objectStore(STORE_ATTACHMENT_PERSIST);
      const countReq = store.count();
      countReq.onsuccess = () => {
        const total = typeof countReq.result === "number" ? countReq.result : 0;
        const toEvict = Math.max(0, total - ATTACHMENT_PERSIST_FIFO_CAP);
        if (toEvict === 0) {
          resolve(0);
          return;
        }
        const rows: Array<{ key: string; updatedAt: number }> = [];
        const collectReq = store.openCursor();
        collectReq.onsuccess = () => {
          const cursor = collectReq.result;
          if (!cursor) {
            rows.sort((a, b) => a.updatedAt - b.updatedAt);
            for (const row of rows.slice(0, toEvict)) {
              store.delete(row.key);
              deleted += 1;
            }
            resolve(deleted);
            return;
          }
          const v = cursor.value as Partial<IdbAttachmentEntry>;
          rows.push({
            key: String(cursor.key),
            updatedAt: typeof v.updatedAt === "number" ? v.updatedAt : 0,
          });
          cursor.continue();
        };
        collectReq.onerror = () => resolve(0);
      };
      countReq.onerror = () => resolve(0);
    } catch {
      resolve(deleted);
    }
  });
}
